/**
 * Container Creation Progress Modal
 *
 * Shows a progress indicator during container creation:
 * 1. Salvataggio dati container
 * 2. Creazione folder in Eve Drive
 * 3. Salvataggio documenti (solo se presenti)
 *
 * Uses UntitledUI components for consistent styling.
 */

import { useTranslation } from 'react-i18next';
import { Modal, ModalOverlay, Dialog } from '@/components/application/modals/modal';
import { ProgressBar } from '@/components/base/progress-indicators/progress-indicators';
import { LoadingIndicator } from '@/components/application/loading-indicator/loading-indicator';
import { CheckCircle, XCircle } from '@untitledui/icons';
import { cx } from '@/utils/cx';

export type CreationStep = 'saving' | 'creating_folder' | 'uploading_documents' | 'completed' | 'completed_with_errors' | 'error';

export interface CreationProgress {
    step: CreationStep;
    uploadProgress?: number;
    uploadedFiles?: number;
    totalFiles?: number;
    error?: string;
    failedFiles?: { name: string; error: string }[];
    containerId?: string;
}

interface ContainerCreationProgressModalProps {
    isOpen: boolean;
    onClose: () => void;
    progress: CreationProgress;
}

interface StepConfig {
    key: string;
    label: string;
    description: string;
}

export function ContainerCreationProgressModal({
    isOpen,
    onClose,
    progress,
}: ContainerCreationProgressModalProps) {
    const { t } = useTranslation('pages:containers');

    // Determine if we should show the uploading_documents step
    const hasDocuments = (progress.totalFiles || 0) > 0;

    const steps: StepConfig[] = [
        {
            key: 'saving',
            label: t('form.progress.saving', 'Salvataggio dati container'),
            description: t('form.progress.savingDesc', 'Salvataggio delle informazioni del container'),
        },
        {
            key: 'creating_folder',
            label: t('form.progress.creatingFolder', 'Creazione folder in Eve Drive'),
            description: t('form.progress.creatingFolderDesc', 'Creazione della cartella per il container'),
        },
        ...(hasDocuments ? [{
            key: 'uploading_documents',
            label: t('form.progress.uploadingDocuments', 'Salvataggio documenti'),
            description: progress.step === 'completed_with_errors'
                ? t('form.progress.uploadFailedCount', '{{failed}} di {{total}} documenti non caricati', {
                    failed: progress.failedFiles?.length || 0,
                    total: progress.totalFiles || 0,
                })
                : progress.step === 'uploading_documents' && progress.totalFiles
                ? t('form.progress.uploadingProgress', 'Caricamento file {{current}} di {{total}}', {
                    current: (progress.uploadedFiles || 0) + 1,
                    total: progress.totalFiles,
                })
                : t('form.progress.uploadingDocumentsDesc', 'Caricamento e associazione dei documenti'),
        }] : []),
    ];

    const getStepIndex = (step: CreationStep): number => {
        switch (step) {
            case 'saving': return 0;
            case 'creating_folder': return 1;
            case 'uploading_documents': return hasDocuments ? 2 : -1;
            case 'completed': return hasDocuments ? 3 : 2;
            case 'completed_with_errors': return hasDocuments ? 3 : 2;
            case 'error': return -1;
            default: return 0;
        }
    };

    const currentStepIndex = getStepIndex(progress.step);

    const getStepStatus = (stepIndex: number, stepKey: string): 'pending' | 'active' | 'completed' | 'error' => {
        if (progress.step === 'error') return 'error';
        if (progress.step === 'completed') return 'completed';
        if (progress.step === 'completed_with_errors') {
            // Documents step shows as error, earlier steps as completed
            return stepKey === 'uploading_documents' ? 'error' : 'completed';
        }
        if (stepIndex < currentStepIndex) return 'completed';
        if (stepIndex === currentStepIndex) return 'active';
        return 'pending';
    };

    // Calculate overall progress percentage
    const overallProgress = progress.step === 'completed' || progress.step === 'completed_with_errors'
        ? 100
        : progress.step === 'error'
        ? 0
        : Math.round((currentStepIndex / steps.length) * 100);

    // Prevent closing during creation
    const handleClose = () => {
        if (progress.step === 'completed' || progress.step === 'completed_with_errors' || progress.step === 'error') {
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <ModalOverlay isOpen={isOpen} onOpenChange={(open) => !open && handleClose()}>
            <Modal>
                <Dialog>
                    <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-lg w-full">
                        {/* Header */}
                        <div className="text-center mb-6">
                            <h2 className="text-xl font-semibold text-primary">
                                {progress.step === 'completed'
                                    ? t('form.progress.completed', 'Container Created!')
                                    : progress.step === 'completed_with_errors'
                                    ? t('form.progress.completedWithErrors', 'Container Created — Some Uploads Failed')
                                    : progress.step === 'error'
                                    ? t('form.progress.error', 'Creation Failed')
                                    : t('form.progress.title', 'Creating Container...')}
                            </h2>
                            <p className="text-sm text-tertiary mt-1">
                                {progress.step === 'completed'
                                    ? t('form.progress.completedDesc', 'Your container has been created successfully')
                                    : progress.step === 'completed_with_errors'
                                    ? t('form.progress.completedWithErrorsDesc', 'The container was created but {{failed}} of {{total}} documents failed to upload', {
                                        failed: progress.failedFiles?.length || 0,
                                        total: progress.totalFiles || 0,
                                    })
                                    : progress.step === 'error'
                                    ? progress.error || t('form.progress.errorDesc', 'An error occurred during creation')
                                    : t('form.progress.subtitle', 'Please wait while we set everything up')}
                            </p>
                        </div>

                        {/* Overall Progress Bar */}
                        {progress.step !== 'completed' && progress.step !== 'completed_with_errors' && progress.step !== 'error' && (
                            <div className="mb-6">
                                <ProgressBar
                                    value={overallProgress}
                                    min={0}
                                    max={100}
                                    labelPosition="right"
                                    progressClassName="bg-gradient-to-r from-brand-500 to-brand-600 transition-all duration-500 ease-out"
                                />
                            </div>
                        )}

                        {/* Steps List */}
                        <div className="space-y-3">
                            {steps.map((step, index) => {
                                const status = getStepStatus(index, step.key);

                                return (
                                    <div
                                        key={step.key}
                                        className={cx(
                                            "flex items-center gap-4 p-4 rounded-lg transition-all duration-300",
                                            status === 'active' && "bg-brand-50 dark:bg-brand-900/20 ring-1 ring-brand-200 dark:ring-brand-800",
                                            status === 'completed' && "bg-success-50 dark:bg-success-900/20",
                                            status === 'error' && "bg-error-50 dark:bg-error-900/20",
                                            status === 'pending' && "bg-secondary/30 opacity-50"
                                        )}
                                    >
                                        {/* Step Icon */}
                                        <div className="flex-shrink-0">
                                            {status === 'completed' && (
                                                <CheckCircle className="h-6 w-6 text-success-600" />
                                            )}
                                            {status === 'active' && (
                                                <LoadingIndicator type="line-spinner" size="sm" />
                                            )}
                                            {status === 'error' && (
                                                <XCircle className="h-6 w-6 text-error-600" />
                                            )}
                                            {status === 'pending' && (
                                                <div className="h-6 w-6 rounded-full border-2 border-tertiary" />
                                            )}
                                        </div>

                                        {/* Step Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between">
                                                <span
                                                    className={cx(
                                                        "text-sm font-medium transition-colors duration-300",
                                                        status === 'completed' && "text-success-700 dark:text-success-400",
                                                        status === 'active' && "text-brand-700 dark:text-brand-400",
                                                        status === 'error' && "text-error-700 dark:text-error-400",
                                                        status === 'pending' && "text-tertiary"
                                                    )}
                                                >
                                                    {step.label}
                                                </span>
                                                <span
                                                    className={cx(
                                                        "text-xs font-medium",
                                                        status === 'completed' && "text-success-600",
                                                        status === 'active' && "text-brand-600",
                                                        status === 'error' && "text-error-600",
                                                        status === 'pending' && "text-quaternary"
                                                    )}
                                                >
                                                    {status === 'completed' && t('form.progress.done', 'Done')}
                                                    {status === 'active' && t('form.progress.inProgress', 'In progress')}
                                                    {status === 'error' && t('form.progress.failed', 'Failed')}
                                                    {status === 'pending' && t('form.progress.waiting', 'Waiting')}
                                                </span>
                                            </div>
                                            <p
                                                className={cx(
                                                    "text-xs mt-1",
                                                    status === 'pending' ? "text-quaternary" : "text-tertiary"
                                                )}
                                            >
                                                {step.description}
                                            </p>

                                            {/* Upload Progress Bar (only for uploading_documents step when active) */}
                                            {step.key === 'uploading_documents' && status === 'active' && progress.uploadProgress !== undefined && (
                                                <div className="mt-3">
                                                    <ProgressBar
                                                        value={progress.uploadProgress}
                                                        min={0}
                                                        max={100}
                                                        labelPosition="right"
                                                        className="h-1.5"
                                                        progressClassName="bg-brand-500"
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        {/* Processing Pulse Effect */}
                                        {status === 'active' && (
                                            <div className="flex-shrink-0">
                                                <div className="h-2 w-2 rounded-full bg-brand-600 animate-pulse" />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Completion State */}
                        {progress.step === 'completed' && (
                            <div className="flex justify-center mt-6 pt-6 border-t border-secondary">
                                <div className="flex items-center justify-center w-16 h-16 rounded-full bg-success-100 dark:bg-success-900/30">
                                    <CheckCircle className="h-8 w-8 text-success-600" />
                                </div>
                            </div>
                        )}

                        {/* Completed with upload errors */}
                        {progress.step === 'completed_with_errors' && progress.failedFiles && (
                            <div className="mt-6 pt-6 border-t border-secondary space-y-4">
                                <div className="bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg p-4">
                                    <p className="text-sm font-medium text-error-700 dark:text-error-400 mb-2">
                                        {t('form.progress.failedUploads', 'Failed uploads:')}
                                    </p>
                                    <ul className="space-y-1.5">
                                        {progress.failedFiles.map((f, i) => (
                                            <li key={i} className="text-sm text-error-600 dark:text-error-400">
                                                <span className="font-medium">{f.name}</span>
                                                <span className="text-error-500 dark:text-error-500"> — {f.error}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="flex justify-center">
                                    <button
                                        onClick={onClose}
                                        className="px-4 py-2 text-sm font-medium text-brand-600 hover:text-brand-700 transition-colors"
                                    >
                                        {t('form.progress.goToContainer', 'Go to container')}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Error Actions */}
                        {progress.step === 'error' && (
                            <div className="flex justify-center mt-6 pt-6 border-t border-secondary">
                                <button
                                    onClick={onClose}
                                    className="px-4 py-2 text-sm font-medium text-error-600 hover:text-error-700 transition-colors"
                                >
                                    {t('form.progress.tryAgain', 'Close and try again')}
                                </button>
                            </div>
                        )}
                    </div>
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
}
